import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import InfoPopover from '../../InfoPopover/InfoPopover';
import { TERM_DEFINITIONS } from '../../InfoPopover/termDefinitions';

import styles from './ChartHtmlLegend.module.css';

const SERVER_WIDTH = 1200;
const readWidth = () => (typeof window === 'undefined' ? SERVER_WIDTH : window.innerWidth);

// Current viewport width, kept in sync with window resizes (listener removed on unmount).
// Hydration-safe: the first render (server and client) always uses SERVER_WIDTH so markup
// matches; the effect then syncs to the real viewport on the client.
export function useViewportWidth() {
    const [width, setWidth] = useState(SERVER_WIDTH);
    useEffect(() => {
        const onResize = () => setWidth(readWidth());
        window.addEventListener('resize', onResize);
        onResize();
        return () => window.removeEventListener('resize', onResize);
    }, []);
    return width;
}

const toItem = ({ text, strokeStyle, hidden, datasetIndex }) => ({
    text,
    color: strokeStyle,
    hidden,
    datasetIndex
});

// Accessible HTML stand-in for the Chart.js canvas legend. The Chart.js plugin feeds it the same
// labels the built-in legend would show (options.plugins.legend.labels.generateLabels), and the
// label click toggles dataset visibility exactly like the built-in legend.
export function useChartHtmlLegend() {
    const chartRef = useRef(null);
    const [items, setItems] = useState([]);

    const plugins = useMemo(
        () => [
            {
                id: 'htmlLegend',
                afterUpdate(chart) {
                    chartRef.current = chart;
                    const next = chart.options.plugins.legend.labels
                        .generateLabels(chart)
                        .map(toItem);
                    setItems(current =>
                        JSON.stringify(current) === JSON.stringify(next) ? current : next
                    );
                }
            }
        ],
        []
    );

    const toggle = useCallback(datasetIndex => {
        const chart = chartRef.current;
        if (!chart) return;
        chart.setDatasetVisibility(datasetIndex, !chart.isDatasetVisible(datasetIndex));
        chart.update();
    }, []);

    return { plugins, items, toggle };
}

// An item may name its glossary term explicitly (`term`) when the chart label differs from it.
const termOf = ({ text, term: explicit }) => {
    if (explicit) return explicit;
    const term = text.split(' (')[0];
    return TERM_DEFINITIONS[term] ? term : null;
};

// `extraItems` are fixed, non-toggling entries ({ text, color }) appended after the series.
// `definitionsOnly` (narrow screens) drops the dataset show/hide entries and keeps just the
// defined terms, each as a non-toggling label with its info button.
export default function ChartHtmlLegend({
    items,
    extraItems = [],
    onToggle,
    definitionsOnly = false,
    fontSize,
    color = '#666'
}) {
    const entries = [...items, ...extraItems].filter(
        item => !definitionsOnly || termOf(item)
    );
    return (
        <ul data-chart-legend aria-label='Chart legend' className={styles.chartLegend} style={{ fontSize, color }}>
            {entries.map(item => {
                const term = termOf(item);
                const toggleable = !definitionsOnly && item.datasetIndex !== undefined;
                const content = (
                    <>
                        <span
                            aria-hidden='true'
                            className={styles.marker}
                            style={{ background: item.color }}
                        />
                        <span style={{ textDecoration: item.hidden ? 'line-through' : 'none' }}>
                            {item.text}
                        </span>
                    </>
                );
                return (
                    <li key={item.text} className={styles.item}>
                        {toggleable ? (
                            <button
                                type='button'
                                className={styles.label}
                                aria-pressed={!item.hidden}
                                onClick={() => onToggle(item.datasetIndex)}
                            >
                                {content}
                            </button>
                        ) : (
                            <span className={styles.label}>{content}</span>
                        )}
                        {term && <InfoPopover term={term} />}
                    </li>
                );
            })}
        </ul>
    );
}
